import { Location } from 'history';
import { CommentFormData } from 'mk2/apps/forum/components/CommentCreateEditForm';
import { ScrollToPosition } from 'mk2/apps/forum/containers/PostDetail/PostDetailPage';
import { PageRef } from 'mk2/apps/forum/schemas';
import { APIFailureAction, APIRequestAction, APISuccessAction } from 'mk2/helpers/action';
import { NormalizedResponse } from 'mk2/helpers/api';
import { Action } from 'redux';

///////////////////////////////////////////////////////////////////
// Post Detail Load

export const POST_DETAIL_FETCH_TRIGGER = 'POST_DETAIL_FETCH_TRIGGER';
export interface PostDetailFetchTriggerAction {
    readonly type: typeof POST_DETAIL_FETCH_TRIGGER;
    readonly topicSlug: string;
    readonly postSlug: string;
    readonly pageRef: PageRef;
    readonly currentUrl: Location;
    readonly numCommentsBeforeLoad: number;
}
export const POST_DETAIL_UPDATE_IN_BACKGROUND_TRIGGER = 'POST_DETAIL_UPDATE_IN_BACKGROUND_TRIGGER';
export interface PostDetailUpdateInBackgroundTriggerAction {
    readonly type: typeof POST_DETAIL_UPDATE_IN_BACKGROUND_TRIGGER;
    readonly topicSlug: string;
    readonly postSlug: string;
    readonly pageRef: PageRef;
    readonly currentUrl: Location;
}
export const POST_DETAIL_FETCH_REQUEST = 'POST_DETAIL_FETCH_REQUEST';
export interface PostDetailFetchRequestAction extends APIRequestAction {
    readonly type: typeof POST_DETAIL_FETCH_REQUEST;
    readonly topicSlug: string;
    readonly postSlug: string;
    readonly pageRef: PageRef;
    readonly brandDaysCampaign: string;
}
export interface PostDetailFetchNormalizedResponse extends NormalizedResponse {
    readonly result: {
        post: number;
        comments: number[];
        commentsWithComplaint: number[];
        contest: number;
        contestAlreadyParticipated: boolean;
        similarPosts: number[];
        recentPosts: number[];
        relatedCounsellings: number[];
        relatedGroups: number[];
        counselling: number;
        brandDays: { posts: number[]; campaign: number; }
        threadConceptsDetails: number[];
    };
}
export const POST_DETAIL_FETCH_SUCCESS = 'POST_DETAIL_FETCH_SUCCESS';
export interface PostDetailFetchSuccessAction extends APISuccessAction<PostDetailFetchNormalizedResponse> {
    readonly type: typeof POST_DETAIL_FETCH_SUCCESS;
    readonly topicSlug: string;
    readonly postSlug: string;
    readonly pageRef: PageRef;
    readonly page: number;
    readonly numberOfPages: number;
    readonly notificationsOn: number[];
}
export const POST_DETAIL_FETCH_FAILURE = 'POST_DETAIL_FETCH_FAILURE';
export interface PostDetailFetchFailureAction extends APIFailureAction {
    readonly type: typeof POST_DETAIL_FETCH_FAILURE;
    readonly topicSlug: string;
    readonly postSlug: string;
    readonly pageRef: PageRef;
}

export const postDetailFetchTrigger = (topicSlug: string, postSlug: string, pageRef: PageRef, currentUrl: Location, numCommentsBeforeLoad: number = 0): PostDetailFetchTriggerAction => ({
    type: POST_DETAIL_FETCH_TRIGGER, topicSlug, postSlug, pageRef, currentUrl, numCommentsBeforeLoad,
});
export const postDetailUpdateInBackgroundTrigger = (topicSlug: string, postSlug: string, pageRef: PageRef, currentUrl: Location): PostDetailUpdateInBackgroundTriggerAction => ({
    type: POST_DETAIL_UPDATE_IN_BACKGROUND_TRIGGER, topicSlug, postSlug, pageRef, currentUrl,
});
export const postDetailFetchApi = {
    request: (topicSlug: string, postSlug: string, pageRef: PageRef, brandDaysCampaign: string): PostDetailFetchRequestAction => ({
        type: POST_DETAIL_FETCH_REQUEST, topicSlug, postSlug, pageRef, brandDaysCampaign,
    }),
    success: (
        topicSlug: string, postSlug: string, pageRef: PageRef, page: number, numberOfPages: number, notificationsOn: number[],
        response: PostDetailFetchNormalizedResponse,
    ): PostDetailFetchSuccessAction => ({
        type: POST_DETAIL_FETCH_SUCCESS, topicSlug, postSlug, pageRef, page, numberOfPages, notificationsOn, response,
    }),
    failure: (topicSlug: string, postSlug: string, pageRef: PageRef, error: any): PostDetailFetchFailureAction => ({
        type: POST_DETAIL_FETCH_FAILURE, topicSlug, postSlug, pageRef, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Post detail - scrollto

export const POST_DETAIL_SCROLLTO_TRIGGER = 'POST_DETAIL_SCROLLTO_TRIGGER';
export interface PostDetailScrollToTriggerAction {
    readonly type: typeof POST_DETAIL_SCROLLTO_TRIGGER;
    readonly scrollto: ScrollToPosition;
}

export const postDetailScrollToTrigger = (scrollto: ScrollToPosition): PostDetailScrollToTriggerAction => ({
    type: POST_DETAIL_SCROLLTO_TRIGGER, scrollto,
});

///////////////////////////////////////////////////////////////////
// Post detail- Time counter for editing

export const EDITABLE_DISABLE = 'EDITABLE_DISABLE';

export interface EditableDisableAction extends Action {
    readonly type: typeof EDITABLE_DISABLE;
    readonly itemId: number;
    readonly page: number;
}

export const editableDisable = (itemId: number, page: number): EditableDisableAction => ({
    type: EDITABLE_DISABLE,
    itemId, page,
});

export const POST_DETAIL_TRACK_TRIGGER = 'POST_DETAIL_TRACK_TRIGGER';

export interface PostDetailTrackTriggerAction extends Action {
    readonly type: typeof POST_DETAIL_TRACK_TRIGGER;
    readonly itemId: number;
}

export const postDetailTrackTrigger = (itemId: number): PostDetailTrackTriggerAction => ({
    type: POST_DETAIL_TRACK_TRIGGER,
    itemId,
});

///////////////////////////////////////////////////////////////////
// Post Detail Comment Create

export const POST_DETAIL_COMMENT_CREATE_TRIGGER = 'POST_DETAIL_COMMENT_CREATE_TRIGGER';
export interface PostDetailCommentCreateTriggerAction {
    readonly type: typeof POST_DETAIL_COMMENT_CREATE_TRIGGER;
    readonly postId: number;
    readonly commentFd: CommentFormData;
}
export const POST_DETAIL_COMMENT_CREATE_REQUEST = 'POST_DETAIL_COMMENT_CREATE_REQUEST';
export interface PostDetailCommentCreateRequestAction extends APIRequestAction {
    readonly type: typeof POST_DETAIL_COMMENT_CREATE_REQUEST;
    readonly postId: number;
    readonly commentFd: CommentFormData;
    readonly key: string;
}
export interface CommentCreateNormalizedResponse extends NormalizedResponse {
    readonly result: { comment: number; post: number; contest: number; contestAlreadyParticipated: boolean; };
}
export const POST_DETAIL_COMMENT_CREATE_SUCCESS = 'POST_DETAIL_COMMENT_CREATE_SUCCESS';
export interface PostDetailCommentCreateSuccessAction extends APISuccessAction<CommentCreateNormalizedResponse> {
    readonly type: typeof POST_DETAIL_COMMENT_CREATE_SUCCESS;
    readonly postId: number;
    readonly commentFd: CommentFormData;
    readonly key: string;
}
export const POST_DETAIL_COMMENT_CREATE_FAILURE = 'POST_DETAIL_COMMENT_CREATE_FAILURE';
export interface PostDetailCommentCreateFailureAction extends APIFailureAction {
    readonly type: typeof POST_DETAIL_COMMENT_CREATE_FAILURE;
    readonly postId: number;
    readonly commentFd: CommentFormData;
    readonly key: string;
}

export const postDetailCommentCreateTrigger =
    (postId: number, commentFd: CommentFormData): PostDetailCommentCreateTriggerAction => ({
        type: POST_DETAIL_COMMENT_CREATE_TRIGGER, postId, commentFd,
    });
export const postDetailCommentCreateApi = {
    request: (postId: number, commentFd: CommentFormData, key: string): PostDetailCommentCreateRequestAction => ({
        type: POST_DETAIL_COMMENT_CREATE_REQUEST, postId, commentFd, key,
    }),
    success: (postId: number, commentFd: CommentFormData, key: string, response: CommentCreateNormalizedResponse): PostDetailCommentCreateSuccessAction => ({
        type: POST_DETAIL_COMMENT_CREATE_SUCCESS, postId, commentFd, key, response,
    }),
    failure: (postId: number, commentFd: CommentFormData, key: string, error: any): PostDetailCommentCreateFailureAction => ({
        type: POST_DETAIL_COMMENT_CREATE_FAILURE, postId, commentFd, key, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Post Detail Comment Delete
export const POST_DETAIL_COMMENT_DELETE_TRIGGER = 'POST_DETAIL_COMMENT_DELETE_TRIGGER';
export interface PostDetailCommentDeleteTriggerAction {
    readonly type: typeof POST_DETAIL_COMMENT_DELETE_TRIGGER;
    readonly id: number;
}
export const POST_DETAIL_COMMENT_DELETE_REQUEST = 'POST_DETAIL_COMMENT_DELETE_REQUEST';
export interface PostDetailCommentDeleteRequestAction extends APIRequestAction {
    readonly type: typeof POST_DETAIL_COMMENT_DELETE_REQUEST;
    readonly id: number;
}
export interface PostDetailCommentDeleteNormalizedResponse extends NormalizedResponse {
    readonly result: { comment: number; };
}
export const POST_DETAIL_COMMENT_DELETE_SUCCESS = 'POST_DETAIL_COMMENT_DELETE_SUCCESS';
export interface PostDetailCommentDeleteSuccessAction extends APISuccessAction<PostDetailCommentDeleteNormalizedResponse> {
    readonly type: typeof POST_DETAIL_COMMENT_DELETE_SUCCESS;
    readonly id: number;
}
export const POST_DETAIL_COMMENT_DELETE_FAILURE = 'POST_DETAIL_COMMENT_DELETE_FAILURE';
export interface PostDetailCommentDeleteFailureAction extends APIFailureAction {
    readonly type: typeof POST_DETAIL_COMMENT_DELETE_FAILURE;
    readonly id: number;
}

export const postDetailCommentDeleteTrigger = (id: number): PostDetailCommentDeleteTriggerAction => ({
    type: POST_DETAIL_COMMENT_DELETE_TRIGGER, id,
});
export const postDetailCommentDeleteApi = {
    request: (id: number): PostDetailCommentDeleteRequestAction => ({
        type: POST_DETAIL_COMMENT_DELETE_REQUEST, id,
    }),
    success: (id: number, response: PostDetailCommentDeleteNormalizedResponse): PostDetailCommentDeleteSuccessAction => ({
        type: POST_DETAIL_COMMENT_DELETE_SUCCESS, id, response,
    }),
    failure: (id: number, error: any): PostDetailCommentDeleteFailureAction => ({
        type: POST_DETAIL_COMMENT_DELETE_FAILURE, id, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Post Detail Comment Move Up

export interface PostDetailMoveUpCommentApiAction extends Action {
    readonly id: number;
}

export const POST_DETAIL_MOVE_UP_COMMENT_API_REQUEST = 'POST_DETAIL_MOVE_UP_COMMENT_API_REQUEST';

export interface PostDetailMoveUpCommentApiRequestAction extends PostDetailMoveUpCommentApiAction, APIRequestAction {
    readonly type: typeof POST_DETAIL_MOVE_UP_COMMENT_API_REQUEST;
}

export const POST_DETAIL_MOVE_UP_COMMENT_API_SUCCESS = 'POST_DETAIL_MOVE_UP_COMMENT_API_SUCCESS';

export interface PostDetailMoveUpCommentApiSuccessAction extends PostDetailMoveUpCommentApiAction, APISuccessAction {
    readonly type: typeof POST_DETAIL_MOVE_UP_COMMENT_API_SUCCESS;
}

export const POST_DETAIL_MOVE_UP_COMMENT_API_FAILURE = 'POST_DETAIL_MOVE_UP_COMMENT_API_FAILURE';

export interface PostDetailMoveUpCommentApiFailureAction extends PostDetailMoveUpCommentApiAction, APIFailureAction {
    readonly type: typeof POST_DETAIL_MOVE_UP_COMMENT_API_FAILURE;
}

export const postDetailMoveUpCommentApi = {
    request: (id: number): PostDetailMoveUpCommentApiRequestAction => ({ type: POST_DETAIL_MOVE_UP_COMMENT_API_REQUEST, id }),
    success: (id: number, response: any): PostDetailMoveUpCommentApiSuccessAction => ({
        type: POST_DETAIL_MOVE_UP_COMMENT_API_SUCCESS,
        id,
        response,
    }),
    failure: (id: number, error: any): PostDetailMoveUpCommentApiFailureAction => ({
        type: POST_DETAIL_MOVE_UP_COMMENT_API_FAILURE,
        id,
        error,
    }),
};

// Post Detail Comment Reply
export const POST_DETAIL_COMMENT_REPLY = 'POST_DETAIL_COMMENT_REPLY';
export interface PostDetailCommentReplyAction extends Action {
    readonly type: typeof POST_DETAIL_COMMENT_REPLY;
    readonly username: string;
    readonly textArea: HTMLTextAreaElement;
}
export const postDetailCommentReply = (username: string, textArea: HTMLTextAreaElement): PostDetailCommentReplyAction => ({
    type: POST_DETAIL_COMMENT_REPLY, username, textArea,
});

// Post detail

// Post detail Comment MoveUp
export const POST_DETAIL_MOVE_UP_COMMENT = 'POST_DETAIL_MOVE_UP_COMMENT';

export interface PostDetailMoveUpCommentAction extends PostDetailMoveUpCommentApiAction {
    readonly type: typeof POST_DETAIL_MOVE_UP_COMMENT;
}

export const postDetailMoveUpComment = (id: number): PostDetailMoveUpCommentAction => ({ type: POST_DETAIL_MOVE_UP_COMMENT, id });

// Action for not authenticated users
export const POST_DETAIL_ADD_COMMENT_UNAUTHENTICATED = 'POST_DETAIL_ADD_COMMENT_UNAUTHENTICATED';

export interface PostDetailAddCommentUnauthenticatedAction extends Action {
    readonly type: typeof POST_DETAIL_ADD_COMMENT_UNAUTHENTICATED;
}

export const postDetailAddCommentUnauthenticated = (): PostDetailAddCommentUnauthenticatedAction => ({
    type: POST_DETAIL_ADD_COMMENT_UNAUTHENTICATED,
});

///////////////////////////////////////////////////////////////////
// Action for store state variable comment to be highlghted
export const POST_DETAIL_TOGGLE_COMMENT_HIGHLIGHT = 'POST_DETAIL_TOGGLE_COMMENT_HIGHLIGHT';

export interface PostDetailToggleCommentHighlightAction extends Action {
    readonly type: typeof POST_DETAIL_TOGGLE_COMMENT_HIGHLIGHT;
    readonly key: string;
    readonly commentId: number | 'last';
}

export const postDetailToggleCommentHighlight = (key: string, commentId: number | 'last'): PostDetailToggleCommentHighlightAction => ({ type: POST_DETAIL_TOGGLE_COMMENT_HIGHLIGHT, key, commentId });

/////////////////////////////////////////////////////////////////////
// Post pin
export interface PostDetailPostStickyApiAction extends Action {
    postId: number;
    sticky: boolean;
}

export const POST_DETAIL_POST_STICKY_API_REQUEST = 'POST_DETAIL_POST_STICKY_API_REQUEST';

export interface PostDetailPostStickyActionuestAction extends PostDetailPostStickyApiAction, APIRequestAction {
    readonly type: typeof POST_DETAIL_POST_STICKY_API_REQUEST;
}

export const POST_DETAIL_POST_STICKY_API_SUCCESS = 'POST_DETAIL_POST_STICKY_API_SUCCESS';

export interface PostDetailPostStickyActioncessAction extends PostDetailPostStickyApiAction, APISuccessAction {
    readonly type: typeof POST_DETAIL_POST_STICKY_API_SUCCESS;
}

export const POST_DETAIL_POST_STICKY_API_FAILURE = 'POST_DETAIL_POST_STICKY_API_FAILURE';

export interface PostDetailPostStickyActionlureAction extends PostDetailPostStickyApiAction, APIFailureAction {
    readonly type: typeof POST_DETAIL_POST_STICKY_API_FAILURE;
}

export const postDetailPostStickyApi = {
    request: (postId: number, sticky: boolean): PostDetailPostStickyActionuestAction => ({ type: POST_DETAIL_POST_STICKY_API_REQUEST, postId, sticky }),
    success: (postId: number, sticky: boolean, response: any): PostDetailPostStickyActioncessAction => ({ type: POST_DETAIL_POST_STICKY_API_SUCCESS, postId, sticky, response }),
    failure: (postId: number, sticky: boolean, error: any): PostDetailPostStickyActionlureAction => ({ type: POST_DETAIL_POST_STICKY_API_FAILURE, postId, sticky, error }),
};

export const POST_DETAIL_POST_STICKY = 'POST_DETAIL_POST_STICKY';

export interface PostDetailPostStickyAction extends PostDetailPostStickyApiAction {
    readonly type: typeof POST_DETAIL_POST_STICKY;
}

export const postDetailPostSticky = (postId: number, sticky: boolean): PostDetailPostStickyAction => ({ type: POST_DETAIL_POST_STICKY, postId, sticky });
